import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2, Trash2, Info, Check, Copy, ChevronRight } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/services/toast.service';
import TenantConfigService, { OidcConfig } from '@/services/tenantConfig.service';
import AuthService from '@/services/auth.service';

interface OidcSsoCardProps {
  tenantConfigId: string;
  customDomain: string;
}

const ROLE_OPTIONS = ['Admin', 'Builder', 'ReadOnly', 'ReadOnly_Partner'];

export default function OidcSsoCard({
  tenantConfigId,
  customDomain,
}: OidcSsoCardProps) {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [issuerUrl, setIssuerUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [scopes, setScopes] = useState('openid email profile');
  const [defaultRole, setDefaultRole] = useState('ReadOnly');
  const [autoProvision, setAutoProvision] = useState(false);
  const [enabled, setEnabled] = useState(true);

  const { data: oidcData, isLoading } = useQuery({
    queryKey: ['oidcConfig', tenantConfigId],
    queryFn: () => TenantConfigService.getInstance().getOidcConfig(tenantConfigId),
    enabled: !!tenantConfigId,
    staleTime: 30 * 1000,
  });

  const existingConfig = oidcData?.record;
  const hasClientSecret = oidcData?.has_client_secret ?? false;

  useEffect(() => {
    if (existingConfig) {
      setIssuerUrl(existingConfig.issuer_url || '');
      setClientId(existingConfig.client_id || '');
      setClientSecret('');
      setScopes(existingConfig.scopes || 'openid email profile');
      setDefaultRole(existingConfig.default_role || 'ReadOnly');
      setAutoProvision(existingConfig.auto_provision ?? false);
      setEnabled(existingConfig.enabled ?? true);
    }
  }, [existingConfig]);

  const isDirty = useMemo(() => {
    if (!existingConfig) {
      return !!(issuerUrl || clientId || clientSecret);
    }
    return (
      issuerUrl !== (existingConfig.issuer_url || '') ||
      clientId !== (existingConfig.client_id || '') ||
      clientSecret !== '' ||
      scopes !== (existingConfig.scopes || 'openid email profile') ||
      defaultRole !== (existingConfig.default_role || 'ReadOnly') ||
      autoProvision !== (existingConfig.auto_provision ?? false) ||
      enabled !== (existingConfig.enabled ?? true)
    );
  }, [existingConfig, issuerUrl, clientId, clientSecret, scopes, defaultRole, autoProvision, enabled]);

  const authService = AuthService.getInstance();
  const appHost = authService.authAppUrl?.replace('https://', '') || '';
  const callbackHost = customDomain || appHost;
  const callbackUrl = callbackHost ? `https://${callbackHost}/api/v1/oidc/callback` : '';

  const saveConfig = useCallback(async () => {
    if (!tenantConfigId) return;
    if (!issuerUrl.trim() || !clientId.trim()) {
      showToast.error('Issuer URL and Client ID are required');
      return;
    }
    // Require client secret on first setup
    if (!existingConfig && !clientSecret.trim()) {
      showToast.error('Client Secret is required');
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, any> = {
        issuer_url: issuerUrl.trim(),
        client_id: clientId.trim(),
        scopes: scopes.trim(),
        default_role: defaultRole,
        auto_provision: autoProvision,
        enabled,
      };
      // Only send client_secret if the user entered a new one
      if (clientSecret.trim()) {
        payload.client_secret = clientSecret.trim();
      }
      await TenantConfigService.getInstance().saveOidcConfig(tenantConfigId, payload);
      setClientSecret('');
      showToast.success('OIDC SSO configuration saved');
      queryClient.invalidateQueries({ queryKey: ['oidcConfig', tenantConfigId] });
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
    } catch (err: any) {
      showToast.error('Failed to save OIDC configuration', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setSaving(false);
    }
  }, [tenantConfigId, issuerUrl, clientId, clientSecret, scopes, defaultRole, enabled, existingConfig, queryClient]);

  const deleteConfig = useCallback(async () => {
    if (!tenantConfigId) return;

    setDeleting(true);
    try {
      await TenantConfigService.getInstance().deleteOidcConfig(tenantConfigId);
      setIssuerUrl('');
      setClientId('');
      setClientSecret('');
      setScopes('openid email profile');
      setDefaultRole('ReadOnly');
      setEnabled(true);
      showToast.success('OIDC SSO configuration removed');
      queryClient.invalidateQueries({ queryKey: ['oidcConfig', tenantConfigId] });
      queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
    } catch (err: any) {
      showToast.error('Failed to remove OIDC configuration', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setDeleting(false);
    }
  }, [tenantConfigId, queryClient]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle>SSO (OIDC)</CardTitle>
            {existingConfig && enabled && (
              <Badge variant="outline" className="border-green-500 text-green-600">
                Active
              </Badge>
            )}
            {existingConfig && !enabled && (
              <Badge variant="outline" className="border-yellow-500 text-yellow-600">
                Disabled
              </Badge>
            )}
          </div>
          {existingConfig && (
            <Switch
              checked={enabled}
              disabled={saving}
              onCheckedChange={async (checked) => {
                setEnabled(checked);
                // Save immediately — the toggle is outside the collapsible
                setSaving(true);
                try {
                  await TenantConfigService.getInstance().saveOidcConfig(tenantConfigId, {
                    ...existingConfig,
                    enabled: checked,
                  });
                  showToast.success(checked ? 'SSO enabled' : 'SSO disabled');
                  queryClient.invalidateQueries({ queryKey: ['oidcConfig', tenantConfigId] });
                  queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
                } catch (err: any) {
                  setEnabled(!checked); // revert on failure
                  showToast.error('Failed to update SSO', {
                    description: err.message || 'An error occurred',
                  });
                } finally {
                  setSaving(false);
                }
              }}
            />
          )}
        </div>
        <CardDescription>
          Configure single sign-on with your identity provider (Okta, Azure AD, Google Workspace, etc.) using OpenID Connect.
        </CardDescription>
      </CardHeader>
      <Collapsible>
        <CardContent className="pt-0 pb-4">
          <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full">
            <ChevronRight className="h-4 w-4 transition-transform [[data-state=open]>&]:rotate-90" />
            {existingConfig ? `Configuration — ${existingConfig.issuer_url}` : 'Configure SSO'}
          </CollapsibleTrigger>
        </CardContent>
        <CollapsibleContent>
        <CardContent className="space-y-4 pt-0">
        {!customDomain && (
          <Alert className="border-blue-200 bg-blue-50">
            <Info className="h-4 w-4" />
            <AlertDescription>
              SSO works best with a custom domain. Without one, the callback URL will use your main Pliable domain.
            </AlertDescription>
          </Alert>
        )}

            <div className="space-y-2">
              <Label>Issuer URL</Label>
              <Input
                value={issuerUrl}
                onChange={(e) => setIssuerUrl(e.target.value)}
                placeholder="https://your-org.okta.com"
              />
              <p className="text-sm text-muted-foreground">
                Your IdP's OIDC issuer URL. We'll auto-discover endpoints from <code>.well-known/openid-configuration</code>.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Client ID</Label>
              <Input
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="0oa1b2c3d4e5f6g7h8i9"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Client Secret</Label>
                {existingConfig && hasClientSecret && !clientSecret && (
                  <span className="inline-flex items-center gap-1 text-xs text-green-600">
                    <Check className="h-3 w-3" />
                    Configured
                  </span>
                )}
              </div>
              <Input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={existingConfig && hasClientSecret ? 'Leave blank to keep current secret' : 'Enter client secret from your IdP'}
              />
            </div>

            <div className="space-y-2">
              <Label>Default Role</Label>
              <Select value={defaultRole} onValueChange={setDefaultRole}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Role assigned to users when they first log in via SSO.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Auto-provision users</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically create accounts for new users from your IdP on their first login.
                </p>
              </div>
              <Switch checked={autoProvision} onCheckedChange={setAutoProvision} />
            </div>

            {callbackUrl && (
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <p className="font-medium">Configure these in your Identity Provider:</p>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Redirect / Callback URI:</p>
                      <div className="flex items-center gap-2">
                        <div className="bg-white rounded border p-2 font-mono text-xs select-all flex-1">
                          {callbackUrl}
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          className="shrink-0 h-8 w-8"
                          onClick={() => {
                            navigator.clipboard.writeText(callbackUrl);
                            showToast.success('Copied to clipboard');
                          }}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Sign-in Redirect / Login URL:</p>
                      <div className="flex items-center gap-2">
                        <div className="bg-white rounded border p-2 font-mono text-xs select-all flex-1">
                          {`https://${callbackHost}/login`}
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          className="shrink-0 h-8 w-8"
                          onClick={() => {
                            navigator.clipboard.writeText(`https://${callbackHost}/login`);
                            showToast.success('Copied to clipboard');
                          }}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium">Sign-out Redirect / Logout URL:</p>
                      <div className="flex items-center gap-2">
                        <div className="bg-white rounded border p-2 font-mono text-xs select-all flex-1">
                          {`https://${callbackHost}/logout`}
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          className="shrink-0 h-8 w-8"
                          onClick={() => {
                            navigator.clipboard.writeText(`https://${callbackHost}/logout`);
                            showToast.success('Copied to clipboard');
                          }}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button onClick={saveConfig} disabled={saving || !isDirty}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : existingConfig ? (
                  'Update SSO Configuration'
                ) : (
                  'Enable SSO'
                )}
              </Button>

              {existingConfig && (
                <Button
                  variant="outline"
                  onClick={deleteConfig}
                  disabled={deleting}
                  className="text-destructive hover:text-destructive"
                >
                  {deleting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Remove SSO
                </Button>
              )}
            </div>
      </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
